import { ColumnShape } from "react-base-table"

import { IReportObs, ObservationStatus, SectionStyles, ValidationSection } from "interfaces/markets"

import HeaderCell from "components/Markets/ExcelDataUpload/Validation/HeaderCell"

import { getOutlierPercentageClassName } from "../utils/validation"

export const MODA = "moda"

export const defaultOutlierFilters = { MoM: 10, YoY: 55, showOutliers: false }

export const sectionStyles: SectionStyles = {
  bg: {
    red: "bg-red-100",
    teal: "bg-green-100",
    yellow: "bg-yellow-100",
    violet: "bg-violet-100",
    gray: "bg-gray-200",
  },
  text: {
    red: "text-red-800 border-red-600",
    teal: "text-teal-800 border-teal-600",
    yellow: "text-yellow-600 border-yellow-500",
    violet: "text-violet-800 border-violet-600",
    gray: "text-gray-400 border-gray-400",
  },
}

export const getSections = (path: string): ValidationSection[] => {
  const isAvailable = path.includes(MODA)

  return [
    {
      label: "Valid Records",
      color: "teal",
      description:
        "Passed all checks and ready for submission. Before submitting, review for outliers using MoM and YoY threshold toggles. Check the Interquartile Range under Lower Bound and Upper Bound columns - values outside these ranges may indicate anomalies. Enable Show Only Outliers to focus on flagged rows.",
      statusId: ObservationStatus.VALID,
      details: true,
      delete: isAvailable,
    },
    {
      label: "Unknown items",
      color: "red",
      description:
        "Markets, commodities, units, or currencies not in the master list. Use Validation Report for details. Contact Global HQ VAM focal point if the master list needs updating.",
      statusId: ObservationStatus.INVALID_KEYS,
      selectable: isAvailable,
      details: true,
      edit: isAvailable,
    },
    {
      label: "Invalid Dates",
      color: "red",
      description: "Invalid date formats. Correct in Excel and upload again. Use Validation Report for details.",
      statusId: ObservationStatus.INVALID_DATE,
      selectable: isAvailable,
      details: true,
      edit: isAvailable,
    },
    {
      label: "Series Not Enabled",
      color: "red",
      description:
        "Records use combinations not allowed by the enabled series list. Contact Global HQ VAM focal point if the series list needs updating.",
      statusId: ObservationStatus.INVALID_SERIES,
      selectable: isAvailable,
      details: true,
      edit: isAvailable,
    },
    {
      label: "Duplicate Records",
      color: "red",
      description:
        "Rows appear more than once or were already uploaded in Data Bridges. Duplicates will be discarded automatically. No action needed.",
      statusId: ObservationStatus.DUPLICATED,
    },
    {
      label: "Discarded Records",
      color: "yellow",
      description:
        "Removed due to critical errors or rules. Records will be discarded automatically. No action needed.",
      statusId: ObservationStatus.DISCARDED,
    },
  ]
}

const formatPercentage = (value: number | undefined): string => {
  if (value === undefined) return ""
  const formatted = value.toFixed(1).replace(/\.0$/, "")
  const sign = value > 0 ? "+" : ""
  return `${sign}${formatted}%`
}

export const outlierColumns = ({
  thresholdMoM,
  thresholdYoY,
}: {
  thresholdMoM: number
  thresholdYoY: number
}): ColumnShape<IReportObs>[] => [
  {
    key: "MoM",
    dataKey: "MoM",
    title: "MoM",
    width: 80,
    sortable: true,
    headerRenderer: ({ column }) => (
      <HeaderCell
        column={column}
        description="Shows the percentage change in price compared to the previous month. Use the slider to set a threshold for detecting unusual monthly variations."
      />
    ),
    cellRenderer: ({ rowData }: { rowData: IReportObs }) => formatPercentage(rowData?.MoM),
    className: ({ rowData }: { rowData: IReportObs }) => getOutlierPercentageClassName(rowData?.MoM, thresholdMoM),
  },
  {
    key: "YoY",
    dataKey: "YoY",
    title: "YoY",
    width: 80,
    sortable: true,
    headerRenderer: ({ column }) => (
      <HeaderCell
        column={column}
        description="Shows the percentage change in price compared to the previous month. Use the slider to set a threshold for detecting unusual monthly variations."
      />
    ),
    cellRenderer: ({ rowData }: { rowData: IReportObs }) => formatPercentage(rowData?.YoY),
    className: ({ rowData }: { rowData: IReportObs }) => getOutlierPercentageClassName(rowData?.YoY, thresholdYoY),
  },
  {
    key: "lowerBound",
    dataKey: "lowerBound",
    title: "Lower Bound",
    width: 130,
    flexShrink: 2,
    resizable: true,
    sortable: true,
    headerRenderer: ({ column }) => (
      <HeaderCell
        column={column}
        description="Minimum expected price based on the Interquartile Range (IQR) across all markets. Prices below this value may indicate potential outliers."
      />
    ),
    cellRenderer: ({ rowData }: { rowData: IReportObs }) => rowData?.lowerBound?.toLocaleString() ?? "",
  },
  {
    key: "upperBound",
    dataKey: "upperBound",
    title: "Upper Bound",
    width: 130,
    resizable: true,
    flexShrink: 2,
    sortable: true,
    headerRenderer: ({ column }) => (
      <HeaderCell
        column={column}
        description="Maximum expected price based on the Interquartile Range (IQR) across all markets. Prices above this value may indicate potential outliers."
      />
    ),
    cellRenderer: ({ rowData }: { rowData: IReportObs }) => rowData?.upperBound?.toLocaleString() ?? "",
  },
]
